import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapSearchBar from "../../shared/components/home/MapSearchBar";
import MapSearchSheet from "../../shared/components/home/MapSearchSheet";
import CountrySelectorSheet from "../../shared/components/home/CountrySelectorSheet";
import ProfileCarousel from "../../shared/components/home/ProfileCarousel";
import ProfileGridView from "../../shared/components/home/ProfileGridView";
import HomeViewToggle from "../../shared/components/home/HomeViewToggle";
import { fetchDiscoverProfiles, invalidateDiscoverCache } from "../../api/discoverApi";
import { buildDiscoverRequestParams } from "../../shared/discover/buildDiscoverQuery";
import {
  buildMapProfiles,
  filterProfiles,
  mapDiscoverItemsToMapProfiles,
  mergeMapProfiles,
  MOCK_LISTINGS,
} from "../../shared/data/mockProfiles";
import {
  getCountriesAlphabetical,
  getCountryMapCenter,
  normalizeCountry,
} from "../../shared/data/locationTaxonomy";
import { getAppLanguages, useLanguage } from "../../shared/i18n";
import {
  DEFAULT_PROFILE_FILTERS,
  normalizeProfileFilters,
  resetProfileFilters,
} from "../../shared/data/profileFilterOptions";
import { loadItem, saveItem, storageKeys } from "../../shared/storage/index";
import { useAuth } from "../../shared/contexts/AuthContext";
import {
  fetchOwnProfile,
  PROFILE_UPDATED_EVENT,
} from "../profile/profileModel";
import "./XDateScreen.css";

const MapExplore = lazy(() => import("../../shared/components/MapExplore"));
const ProfileDetailSheet = lazy(() => import("../../shared/components/home/ProfileDetailSheet"));

const EMPTY_PROFILES = [];

export default function XDateScreen({ isTabActive = true }) {
  const { language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [detailProfile, setDetailProfile] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [browseCountry, setBrowseCountry] = useState(
    () => normalizeCountry(loadItem(storageKeys.browseCountry, "Switzerland")),
  );
  const [profileFilters, setProfileFilters] = useState(
    () => normalizeProfileFilters(loadItem(storageKeys.profileFilters, DEFAULT_PROFILE_FILTERS)),
  );
  const [liveDiscoverItems, setLiveDiscoverItems] = useState([]);
  const [discoverError, setDiscoverError] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [headerCollapse, setHeaderCollapse] = useState(0);
  const discoverAbortRef = useRef(null);
  const discoverRequestIdRef = useRef(0);

  const mapCenter = useMemo(
    () => getCountryMapCenter(browseCountry),
    [browseCountry],
  );

  const discoverParams = useMemo(
    () => buildDiscoverRequestParams(browseCountry),
    [browseCountry],
  );

  const refreshLiveProfiles = useCallback(async (
    country = browseCountry,
    { force = false, cities = [] } = {},
  ) => {
    if (force) {
      invalidateDiscoverCache();
    }

    discoverAbortRef.current?.abort();
    const controller = new AbortController();
    discoverAbortRef.current = controller;
    const requestId = discoverRequestIdRef.current + 1;
    discoverRequestIdRef.current = requestId;

    try {
      const result = await fetchDiscoverProfiles({
        country,
        cities,
        limit: 50,
        signal: controller.signal,
      });
      if (controller.signal.aborted || requestId !== discoverRequestIdRef.current) {
        return [];
      }
      setDiscoverError(null);
      setLiveDiscoverItems(result.items || []);
      return result.items || [];
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "AbortError") {
        return [];
      }
      if (requestId !== discoverRequestIdRef.current) {
        return [];
      }
      console.warn("Failed to load live discover profiles", error);
      setDiscoverError("Could not reach the server. Start the backend and reload.");
      setLiveDiscoverItems([]);
      return [];
    }
  }, [browseCountry]);

  useEffect(() => {
    if (!isTabActive) return undefined;

    refreshLiveProfiles(browseCountry, discoverParams);
    return () => {
      discoverRequestIdRef.current += 1;
      discoverAbortRef.current?.abort();
    };
  }, [browseCountry, isTabActive, refreshLiveProfiles, discoverParams]);

  useEffect(() => {
    const handleProfileUpdated = async () => {
      try {
        const ownProfile = await fetchOwnProfile(user?.id);
        const nextBrowseCountry = ownProfile?.country
          ? normalizeCountry(ownProfile.country)
          : browseCountry;

        if (nextBrowseCountry !== browseCountry) {
          setBrowseCountry(nextBrowseCountry);
          saveItem(storageKeys.browseCountry, nextBrowseCountry);
        }

        const items = await refreshLiveProfiles(nextBrowseCountry, {
          force: true,
          ...discoverParams,
        });

        if (
          ownProfile?.locationSource === "gps"
          && ownProfile.latitude != null
          && ownProfile.longitude != null
          && ownProfile.id
          && items.some((item) => item.id === ownProfile.id)
        ) {
          setSelectedProfileId(ownProfile.id);
        }
      } catch (error) {
        console.warn("Failed to refresh own profile for map focus", error);
      }
    };

    window.addEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdated);
  }, [refreshLiveProfiles, user?.id, browseCountry, discoverParams]);

  const useDemoMocks = import.meta.env.VITE_DEMO_MOCKS === "true";

  const allProfiles = useMemo(() => {
    const liveProfiles = mapDiscoverItemsToMapProfiles(liveDiscoverItems, mapCenter);
    const mockProfiles = useDemoMocks
      ? buildMapProfiles(mapCenter, MOCK_LISTINGS, browseCountry)
      : [];
    return mergeMapProfiles(liveProfiles, mockProfiles, mapCenter);
  }, [mapCenter, browseCountry, liveDiscoverItems, useDemoMocks]);

  const filteredProfiles = useMemo(
    () => filterProfiles(allProfiles, profileFilters),
    [allProfiles, profileFilters],
  );

  const filterSheetProfiles = isFilterOpen ? allProfiles : EMPTY_PROFILES;

  const defaultProfileId = useMemo(() => {
    if (!filteredProfiles.length) return null;
    const nearest = filteredProfiles.reduce((best, profile) => {
      if (!best) return profile;
      return (profile.distanceKm ?? Infinity) < (best.distanceKm ?? Infinity) ? profile : best;
    }, null);
    return nearest?.id ?? filteredProfiles[0].id;
  }, [filteredProfiles]);

  const effectiveSelectedId = useMemo(() => {
    if (selectedProfileId && filteredProfiles.some((profile) => profile.id === selectedProfileId)) {
      return selectedProfileId;
    }
    return defaultProfileId;
  }, [selectedProfileId, defaultProfileId, filteredProfiles]);

  const focusProfile = useMemo(() => {
    if (!effectiveSelectedId) return null;
    return filteredProfiles.find((profile) => profile.id === effectiveSelectedId) ?? null;
  }, [filteredProfiles, effectiveSelectedId]);

  useEffect(() => {
    if (!filteredProfiles.length) {
      setSelectedProfileId(null);
      return;
    }

    setSelectedProfileId((current) => {
      if (current && filteredProfiles.some((profile) => profile.id === current)) {
        return current;
      }
      return defaultProfileId;
    });
  }, [filteredProfiles, defaultProfileId]);

  const handleOpenProfile = useCallback((profile) => {
    setDetailProfile(profile);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setDetailProfile(null);
  }, []);

  const handleApplyFilters = useCallback((next) => {
    const normalized = normalizeProfileFilters(next);
    setProfileFilters(normalized);
    saveItem(storageKeys.profileFilters, normalized);
    invalidateDiscoverCache();
  }, []);

  const handleResetFilters = useCallback(() => {
    const cleared = resetProfileFilters();
    setProfileFilters(cleared);
    saveItem(storageKeys.profileFilters, cleared);
    invalidateDiscoverCache();
  }, []);

  const handleSelectCountry = useCallback((countryName) => {
    setBrowseCountry(countryName);
    setSelectedProfileId(null);
    saveItem(storageKeys.browseCountry, countryName);
    handleResetFilters();
  }, [handleResetFilters]);

  const handleProfileSelect = useCallback((profile) => {
    setSelectedProfileId(profile.id);
  }, []);

  const handleGridProfilePress = useCallback((profile) => {
    setSelectedProfileId(profile.id);
    setDetailProfile(profile);
  }, []);

  const handleViewModeChange = useCallback((next) => {
    setViewMode(next);
    setHeaderCollapse(0);
  }, []);

  const handleGridScrollCollapse = useCallback((collapse) => {
    setHeaderCollapse(collapse);
  }, []);

  const browseCountries = useMemo(() => getCountriesAlphabetical(), []);
  const appLanguageOptions = useMemo(() => getAppLanguages(), []);

  return (
    <div
      className={[
        "home-map-screen",
        viewMode === "grid" ? "home-map-screen--grid" : "",
        headerCollapse > 0.55 ? "home-map-screen--header-collapsed" : "",
      ].filter(Boolean).join(" ")}
      style={{ "--header-collapse": headerCollapse }}
    >
      <div className="home-map-screen__stage">
        <div key={viewMode} className={`home-map-screen__stage-pane home-map-screen__stage-pane--${viewMode}`}>
          {viewMode === "map" ? (
            <div className="home-map-screen__map">
              <Suspense fallback={null}>
                <MapExplore
                  isActive={isTabActive}
                  searchCenter={mapCenter}
                  focusProfile={focusProfile}
                  profiles={filteredProfiles}
                  selectedProfileId={effectiveSelectedId}
                  onProfileSelect={handleProfileSelect}
                  fitToProfilesOnLoad
                />
              </Suspense>
            </div>
          ) : (
            <ProfileGridView
              profiles={filteredProfiles}
              onProfilePress={handleGridProfilePress}
              onScrollCollapse={handleGridScrollCollapse}
            />
          )}
        </div>
      </div>

      <div className="home-map-screen__header">
        <div className="home-map-screen__header-row">
          <MapSearchBar
            onPress={() => setIsFilterOpen(true)}
            onCountryPress={() => setIsCountryOpen(true)}
            browseCountry={browseCountry}
            collapseProgress={viewMode === "grid" ? headerCollapse : 0}
          />
          <HomeViewToggle viewMode={viewMode} onViewModeChange={handleViewModeChange} />
        </div>
        {discoverError ? (
          <p className="home-map-screen__discover-error" role="status">
            {discoverError}
          </p>
        ) : null}
      </div>

      {viewMode === "map" ? (
        <ProfileCarousel
          profiles={filteredProfiles}
          selectedProfileId={effectiveSelectedId}
          onSelectedChange={setSelectedProfileId}
          onProfilePress={handleOpenProfile}
        />
      ) : null}

      <MapSearchSheet
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={profileFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        browseCountry={browseCountry}
        profiles={filterSheetProfiles}
      />

      <CountrySelectorSheet
        open={isCountryOpen}
        onClose={() => setIsCountryOpen(false)}
        countries={browseCountries}
        languages={appLanguageOptions}
        selectedCountry={browseCountry}
        selectedLanguage={language}
        onSelectCountry={handleSelectCountry}
        onSelectLanguage={setLanguage}
      />

      {detailProfile ? (
        <Suspense fallback={null}>
          <ProfileDetailSheet
            profile={detailProfile}
            open={Boolean(detailProfile)}
            onClose={handleCloseProfile}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
